import { useState } from 'react';
import PropTypes from 'prop-types';
import AirportEditForm from './AirportEditForm';

const PencilIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    className="w-5 h-5"
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth="2" 
      d="M15.232 5.232l1.687-1.688a1.875 1.875 0 112.652 2.652L6.5 21h-3v-3L16.732 3.732z" 
    />
  </svg>
);

const AirportDetailView = ({ airport, onEdit }) => (
  <div className="relative">
    <div className="space-y-1">
      <h1 className="text-2xl font-bold">
        <span className="font-mono text-text-primary dark:text-text-primary-dark flex items-center gap-1">
          [{airport.airportCode}]
          <button
            onClick={onEdit}
            className="p-1 rounded-lg transition-colors text-text-tertiary hover:text-text-link dark:text-text-tertiary-dark dark:hover:text-text-link-dark hover:bg-surface-hover dark:hover:bg-surface-hover-dark"
            title="Edit airport details"
          >
            <PencilIcon />
          </button>
        </span>
        <span className="text-text-primary dark:text-text-primary-dark">
          {airport.airportName}
        </span>
      </h1>
      <div className="flex items-center gap-2">
        <p className="text-text-primary dark:text-text-primary-dark">
          {airport.city}, {airport.state}
        </p>
        <span className="text-text-primary dark:text-text-primary-dark">•</span>
        <p className="text-text-primary dark:text-text-primary-dark">
          {airport.elevation} ft
        </p>
      </div>
      <div className="flex flex-wrap gap-4 mt-2">
        {airport.atisNumber && (
          <p className="text-text-primary dark:text-text-primary-dark">
            <span className="font-medium">ATIS:</span>{' '}
            <a 
              href={`tel:${airport.atisNumber.replace(/[^0-9]/g, '')}`}
              className="text-text-link dark:text-text-link-dark hover:text-text-link-hover dark:hover:text-text-link-hover-dark"
            >
              {airport.atisNumber}
            </a>
          </p>
        )}
        {airport.awosNumber && (
          <p className="text-text-primary dark:text-text-primary-dark">
            <span className="font-medium">AWOS:</span>{' '}
            <a 
              href={`tel:${airport.awosNumber.replace(/[^0-9]/g, '')}`}
              className="text-text-link dark:text-text-link-dark hover:text-text-link-hover dark:hover:text-text-link-hover-dark"
            >
              {airport.awosNumber}
            </a>
          </p>
        )}
      </div>
      <div className="mt-2 text-sm text-text-muted dark:text-text-muted-dark">
        Last modified: {new Date(airport.dateModified).toLocaleDateString()}
      </div>
    </div>
  </div>
);

const AirportDetailContainer = ({ airport: initialAirport, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [airport, setAirport] = useState(initialAirport);

  const handleSave = async (updatedData) => {
    try {
      await onSave(updatedData);
      setAirport({
        ...updatedData,
        dateModified: new Date().toISOString()
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving airport data:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  return isEditing ? (
    <AirportEditForm
      airport={airport}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  ) : (
    <AirportDetailView
      airport={airport}
      onEdit={() => setIsEditing(true)}
    />
  );
};

AirportDetailView.propTypes = {
  airport: PropTypes.shape({
    airportCode: PropTypes.string.isRequired,
    airportName: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    elevation: PropTypes.string.isRequired,
    atisNumber: PropTypes.string,
    awosNumber: PropTypes.string,
    dateModified: PropTypes.string.isRequired,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
};

AirportDetailContainer.propTypes = {
  airport: PropTypes.shape({
    airportCode: PropTypes.string.isRequired,
    airportName: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    elevation: PropTypes.string.isRequired,
    atisNumber: PropTypes.string,
    awosNumber: PropTypes.string,
    dateModified: PropTypes.string.isRequired,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
};

export default AirportDetailContainer; 